/**
 * Your access (Figma 09 · The client's Settings C01–C08): a guest's Settings
 * home. Not built, no backend yet: "Links sent to you" (share links were
 * removed), "Your team" and "Email <owner>" (guests cannot read the
 * workspace's members), and Notifications.
 */
import { Link, useNavigate } from "react-router";
import { Building2, ChevronRight, Gauge, Hourglass, Palette, Share2 } from "lucide-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useGetBrands, type Brand } from "@/hooks/useGetBrands";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useAppearanceStore } from "@/stores/appearanceStore";
import { useAcceptInvite, useDeclineInvite, useMyInvites } from "@/app/routes/dashboard/settings/api/useOrganization";
import { KitButton } from "../../kit/Button";
import { BrandMark } from "../../kit/BrandPicker";
import { ErrorState } from "../../ViewStates";
import { CardLoading, DetailCard, GlyphTile, DetailHead, Fact, Facts, GroupLabel, PersonMark, ScopeTile, StatusLabel } from "../parts/SettingsParts";
import { formatDay, listJoin, plural } from "../parts/settingsFormat";

const THEME_LABEL = { system: "System", light: "Light", dark: "Dark" } as const;

function HubRow({ lead, title, sub, to, onPress }: { lead: React.ReactNode; title: string; sub: string; to?: string; onPress?: () => void }) {
  const body = (
    <>
      {lead}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[13.5px] font-semibold text-co-text">{title}</span>
        <span className="text-[12px] leading-[1.4] text-co-muted">{sub}</span>
      </span>
      <ChevronRight aria-hidden className="size-3.5 shrink-0 text-co-faint" />
    </>
  );
  const className = "flex w-full items-center gap-3 rounded-row px-3 py-2.5 text-left transition-colors hover:bg-co-hover";
  return to ? (
    <Link to={to} className={`unstyled-link ${className}`}>
      {body}
    </Link>
  ) : (
    <button type="button" onClick={onPress} className={className}>
      {body}
    </button>
  );
}

export default function YourAccessPage() {
  const navigate = useNavigate();
  const { user, selectedOrganization } = useCurrentUser();
  const workspaceCount = user?.userOrganizations?.length ?? 0;
  const setSelectedBrandId = useWorkspaceStore((s) => s.setSelectedBrandId);
  const theme = useAppearanceStore((s) => s.theme);
  const brandsQuery = useGetBrands();
  const { data: invites } = useMyInvites();
  const accept = useAcceptInvite();
  const decline = useDeclineInvite();
  const brands = brandsQuery.data ?? [];
  const pending = invites ?? [];

  const openCalendar = (brand: Brand) => {
    setSelectedBrandId(brand.id);
    navigate("/dashboard/content-calendar");
  };

  const lead =
    brands.length === 0
      ? "You have no brands to review here yet."
      : `You can review ${brands.length === 1 ? "one brand" : plural(brands.length, "brand")} in Creo: ${listJoin(brands.map((b) => b.name))}.`;

  return (
    <div className="mx-auto flex w-full max-w-[560px] flex-col gap-6 py-4">
      <header className="flex flex-col gap-3">
        <h1 className="font-display text-[36px] font-semibold leading-[1.05] tracking-normal text-co-text md:text-[40px]">Your access</h1>
        <p className="text-[15px] leading-[1.55] text-co-text2">{pending.length ? `${lead} You have an invitation waiting.` : lead}</p>
      </header>

      {pending.map((invite) => (
        <DetailCard key={invite.id}>
          <DetailHead
            lead={<ScopeTile name={invite.brands?.[0]?.name ?? invite.organization.name} size={56} />}
            title={invite.brands?.length ? listJoin(invite.brands.map((b) => b.name)) : invite.organization.name}
            sub={`${invite.inviter.name || invite.inviter.email || "Someone"} invited you to review it`}
            aside={<StatusLabel icon={Hourglass}>Waiting for you</StatusLabel>}
          />
          <Facts>
            <Fact label="You would see">
              {invite.brands?.length ? `${listJoin(invite.brands.map((b) => b.name))}’s calendar and concepts` : "The brands you were given"}
              , to review and comment
            </Fact>
            <Fact label="Ends">{formatDay(invite.expiresAt)}</Fact>
          </Facts>
          <div className="flex items-center gap-3">
            <KitButton onClick={() => accept.mutate(invite.id)} isDisabled={accept.isPending}>
              Accept
            </KitButton>
            <KitButton variant="ghost" onClick={() => decline.mutate(invite.id)} isDisabled={decline.isPending}>
              Decline
            </KitButton>
          </div>
        </DetailCard>
      ))}

      {brandsQuery.isLoading ? (
        <CardLoading caption="Loading your access" rows={3} />
      ) : brandsQuery.isError ? (
        <ErrorState
          title="Your access did not load"
          body="Creo could not read it. Nothing has changed."
          onRetry={() => void brandsQuery.refetch()}
          minHeight={240}
        />
      ) : (
        <nav className="flex flex-col gap-0.5 rounded-panel bg-co-surface p-2">
          {brands.length ? <GroupLabel>Brands you can open</GroupLabel> : null}
          {brands.map((brand) => (
            <HubRow
              key={brand.id}
              lead={<BrandMark brand={brand} size={36} />}
              title={brand.name}
              sub="Calendar and concepts, to review and comment"
              onPress={() => openCalendar(brand)}
            />
          ))}
          <GroupLabel>Your settings</GroupLabel>
          <HubRow
            lead={<PersonMark name={user?.name || user?.email} picture={user?.profilePicture} />}
            title="Profile and sign-in"
            sub={`${user?.name || user?.email} · signs in with ${user?.hasPassword ? "a password" : "Google"}`}
            to=".?page=profile"
          />
          <HubRow
            lead={<GlyphTile icon={Building2} />}
            title="Workspaces"
            sub={
              workspaceCount > 1
                ? `${selectedOrganization?.organization.name ?? "This workspace"} · switch to another of your ${workspaceCount}`
                : (selectedOrganization?.organization.name ?? "The workspaces you belong to")
            }
            to=".?page=workspaces"
          />
          <HubRow lead={<GlyphTile icon={Palette} />} title="Appearance" sub={THEME_LABEL[theme]} to=".?page=appearance" />
          <HubRow lead={<GlyphTile icon={Share2} />} title="Social accounts" sub="Connect your brand’s accounts" to=".?page=social-accounts" />
          <HubRow lead={<GlyphTile icon={Gauge} />} title="Your usage" sub="Your monthly AI allowance" to=".?page=your-usage" />
        </nav>
      )}

      <Facts>
        <Fact label="Your login">{user?.email}</Fact>
        <Fact label="To change it">Ask the team that invited you for another brand, or to stop your access</Fact>
      </Facts>

      {brands.length === 1 ? (
        <KitButton className="self-start" onClick={() => openCalendar(brands[0])}>
          Open {brands[0].name}’s calendar
        </KitButton>
      ) : null}
    </div>
  );
}
